import { AUTH_CONFIG } from './auth0-variables';

const TENANT_QUERY_KEYS = ['tenant', 'company', 'domain', 'sso', 'client'];
const EMAIL_QUERY_KEYS = ['login_hint', 'email'];

function readFirstParam(params, keys) {
  for (const key of keys) {
    const value = params.get(key);
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return '';
}

function extractEmailDomain(value) {
  const normalized = String(value || '').trim().toLowerCase();
  const atIndex = normalized.lastIndexOf('@');
  if (atIndex < 0 || atIndex === normalized.length - 1) return '';
  return normalized.slice(atIndex + 1);
}

// A `?tenant=`-style value counts as a caller-supplied domain when it looks
// like one: contains a dot, no '@', no whitespace. There is no client-side
// tenant list to validate it against any more (removed by the 2026-08
// security fix, and the loader that fetched it is gone too — the browser
// never holds the list of customer domains) — the server is the sole
// authority on whether it actually maps to anything, via /api/auth/check-sso.
function looksLikeDomain(value) {
  const trimmed = String(value || '').trim();
  if (!trimmed) return false;
  if (/\s/.test(trimmed)) return false;
  if (trimmed.indexOf('@') >= 0) return false;
  return trimmed.indexOf('.') >= 0;
}

export function resolveSsoRoute(locationLike = window.location) {
  const params = new URLSearchParams(locationLike?.search || '');
  const explicitConnection = readFirstParam(params, ['connection']);
  const explicitOrganization = readFirstParam(params, ['organization']);
  const loginHint = readFirstParam(params, EMAIL_QUERY_KEYS);
  const requestedTenant = readFirstParam(params, TENANT_QUERY_KEYS);
  const emailDomain = extractEmailDomain(loginHint);

  // matchedDomain is simply the caller-supplied domain, taken at face value —
  // the client can no longer validate it against a tenant list. It is used
  // only as a non-sensitive UX prefill (domain_hint); the server is the sole
  // authority on whether SSO actually applies, via /api/auth/check-sso.
  const matchedDomain = looksLikeDomain(requestedTenant)
    ? requestedTenant.toLowerCase()
    : emailDomain || '';

  return {
    explicitConnection,
    explicitOrganization,
    loginHint,
    matchedDomain,
  };
}

export function getSsoLoginOptions(locationLike = window.location) {
  const { explicitConnection, explicitOrganization, loginHint, matchedDomain } =
    resolveSsoRoute(locationLike);

  const options = {};

  // The client no longer holds any tenant's connection/organization — removed
  // by the 2026-08 security fix (they were an enumerable list of every SSO
  // client's Auth0 connection name, served to anonymous callers). A proactive
  // redirect to a specific connection/organization is therefore only possible
  // when the caller supplied ?connection= and/or ?organization= explicitly.
  if (explicitConnection) {
    options.connection = explicitConnection;
  }

  if (explicitOrganization) {
    options.organization = explicitOrganization;
  }

  // login_hint/domain_hint are plain UX prefills, not routing secrets — safe
  // to pass whenever known, so a `?tenant=<domain>` link or a typed email
  // still lands on a hinted Auth0 screen instead of the fully bare Universal
  // Login page.
  if (loginHint) {
    options.login_hint = loginHint;
  }

  if (matchedDomain) {
    options.domain_hint = matchedDomain;
  }

  return { options };
}

export function getLoginRedirectOptionsForIdentifier(identifier) {
  const loginHint = String(identifier || '').trim();
  const emailDomain = extractEmailDomain(loginHint);
  const isEmail = Boolean(emailDomain);

  const options = {};

  if (loginHint) options.login_hint = loginHint;

  if (!isEmail && AUTH_CONFIG.basicConnection) {
    options.connection = AUTH_CONFIG.basicConnection;
  }

  // The client holds no domain list at all any more (the browser-side loader
  // that used to fetch one, and the endpoint it read, are both gone). It can
  // therefore never rule out an email being SSO: every email is "deferred" to
  // the server, which is the sole authority via /api/auth/check-sso. Only a
  // non-email identifier has
  // no possible SSO signal at all and can safely read as "basic". No
  // `connection`/`organization` here even for an email — Home.js's handleLogin
  // always resolves those per-identifier via /api/auth/check-sso first; this
  // options object is only used as a fallback if that call fails or is
  // inconclusive.
  if (isEmail) {
    options.domain_hint = emailDomain;
  }

  return {
    loginHint,
    isEmail,
    mode: isEmail ? 'deferred' : 'basic',
    options,
  };
}
